import type { ComponentProps, ReactNode } from "react";
import type { VariantProps } from "class-variance-authority";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, ChevronRight, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { productizeError } from "@/lib/product-error";

import { Button, buttonVariants } from "@/components/ui/button";
import { Badge, badgeVariants } from "@/components/ui/badge";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";

export {
  cn,
  Button,
  buttonVariants,
  Badge,
  badgeVariants,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Label,
  Textarea,
  Checkbox,
  Alert,
  AlertDescription,
  AlertTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Separator,
  ScrollArea,
  ScrollBar,
  Skeleton,
  ToggleGroup,
  ToggleGroupItem,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
};

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

const TITLE_MARKER =
  "relative pl-3 before:absolute before:left-0 before:top-0.5 before:bottom-0.5 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-primary before:to-chart-5";

export function PageHeader({
  title,
  description,
  actions
}: {
  title: string;
  description: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="relative pl-4">
        <span
          aria-hidden="true"
          className="absolute top-1 bottom-1 left-0 w-1 rounded-full bg-gradient-to-b from-primary to-chart-2 shadow-[0_0_12px_oklch(0.56_0.14_244/0.45)]"
        />
        <h1 className="font-display text-[26px] leading-[1.2] font-bold text-foreground">
          {title}
        </h1>
        {typeof description === "string" ? (
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{description}</p>
        ) : (
          <div className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{description}</div>
        )}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function SectionCard({
  title,
  description,
  action,
  children,
  className,
  bodyClassName
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <Card className={cn("gap-0 py-0", className)}>
      {title || action ? (
        <CardHeader className="border-b pt-4 pb-4">
          {title ? (
            <CardTitle className={cn("font-display text-base font-bold", TITLE_MARKER)}>{title}</CardTitle>
          ) : null}
          {description ? <CardDescription>{description}</CardDescription> : null}
          {action ? <CardAction>{action}</CardAction> : null}
        </CardHeader>
      ) : null}
      <CardContent className={cn("py-5", bodyClassName)}>{children}</CardContent>
    </Card>
  );
}

const STAT_TONES: Record<"default" | "ok" | "warn" | "error", { bar: string; chip: string }> = {
  default: { bar: "bg-slate-300", chip: "bg-slate-100 text-slate-600" },
  ok: { bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-600" },
  warn: { bar: "bg-amber-500", chip: "bg-amber-50 text-amber-600" },
  error: { bar: "bg-red-500", chip: "bg-red-50 text-red-600" }
};

export function StatCard({
  label,
  value,
  detail,
  icon,
  tone = "default",
  className
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  icon?: ReactNode;
  tone?: keyof typeof STAT_TONES;
  className?: string;
}) {
  const t = STAT_TONES[tone];
  return (
    <Card
      className={cn(
        "relative overflow-hidden py-5 transition-all hover:-translate-y-0.5 hover:shadow-md",
        className
      )}
    >
      <span aria-hidden="true" className={cn("absolute inset-y-3 left-0 w-1 rounded-r", t.bar)} />
      <div className="flex items-start gap-3 px-6">
        {icon ? (
          <div className={cn("grid size-9 shrink-0 place-items-center rounded-lg", t.chip)}>
            {icon}
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="text-label">{label}</div>
          <div className="tnum font-display text-3xl leading-tight font-bold text-foreground">
            {value}
          </div>
          {detail ? (
            <div className="mt-1 truncate text-xs text-muted-foreground">{detail}</div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

const BADGE_TONES: Record<"ok" | "warn" | "error" | "neutral", string> = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warn: "border-amber-200 bg-amber-50 text-amber-700",
  error: "border-red-200 bg-red-50 text-red-700",
  neutral: "border-slate-200 bg-slate-100 text-slate-600"
};

export function StatusBadge({
  status,
  children,
  className
}: {
  status: keyof typeof BADGE_TONES;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        BADGE_TONES[status],
        className
      )}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

export function LoadingState({ label = "正在加载" }: { label?: string }) {
  return (
    <div
      role="status"
      className="flex min-h-[150px] items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card/60 px-6 py-10 text-sm text-muted-foreground"
    >
      <Spinner className="size-4 text-primary" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  detail,
  action
}: {
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <Empty className="border-border/70 bg-muted/30">
      <EmptyHeader>
        <EmptyMedia variant="icon" className="animate-[float-soft_4.5s_ease-in-out_infinite]">
          <Inbox strokeWidth={1.6} />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{detail}</EmptyDescription>
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const product = productizeError(error);
  return (
    <Alert variant="destructive">
      <AlertCircle />
      <AlertTitle>{product.title}</AlertTitle>
      <AlertDescription>
        {product.message}
        {product.requestId ? (
          <span className="mt-1 block text-xs opacity-80">请求编号 {product.requestId}</span>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

export function ForbiddenState({
  title = "没有访问权限",
  detail = "当前账号无法执行此操作。如需帮助，请联系平台操作员。"
}: {
  title?: string;
  detail?: string;
}) {
  return (
    <Alert variant="destructive">
      <AlertCircle />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{detail}</AlertDescription>
    </Alert>
  );
}

/** Outer three-column contract for document workspace (rail / center / inspector). */
export function WorkspaceThreeColumn({
  rail,
  center,
  inspector,
  showRail = false,
  showInspector = false,
  className
}: {
  rail: ReactNode;
  center: ReactNode;
  inspector: ReactNode;
  showRail?: boolean;
  showInspector?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "workspace-three-column min-h-[28rem] flex-1",
        showRail && "workspace-show-rail",
        showInspector && "workspace-show-inspector",
        className
      )}
    >
      <aside className="workspace-rail" data-slot="workspace-rail">
        {rail}
      </aside>
      <section className="workspace-center" data-slot="workspace-center">
        {center}
      </section>
      <aside className="workspace-inspector" data-slot="workspace-inspector">
        {inspector}
      </aside>
    </div>
  );
}

export function ConfirmDestructive({
  open,
  title,
  description,
  confirmLabel = "确认删除",
  cancelLabel = "取消",
  pending = false,
  onConfirm,
  onOpenChange
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  pending?: boolean;
  onConfirm(): void;
  onOpenChange(open: boolean): void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button type="button" variant="destructive" disabled={pending} onClick={onConfirm}>
            {pending ? <Spinner className="size-4" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Modal({
  title,
  description,
  children,
  onClose,
  wide = false,
  className
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  onClose(): void;
  wide?: boolean;
  className?: string;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={cn(
          "max-h-[calc(100vh-3rem)] gap-0 overflow-y-auto rounded-2xl p-0 shadow-xl sm:max-w-lg",
          wide && "sm:max-w-3xl",
          className
        )}
      >
        <DialogHeader className="gap-1.5 border-b border-border/80 px-6 py-5 text-left">
          <DialogTitle className={cn(TITLE_MARKER, "text-base")}>{title}</DialogTitle>
          {description ? (
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {/* Keep px-6 py-6 so existing modal footers using -mx-6 -mb-6 still align. */}
        <div className="px-6 py-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

export function Toast({ message, onClose }: { message: string; onClose(): void }) {
  return (
    <div
      role="status"
      className="fixed right-6 bottom-6 z-[120] flex min-h-11 items-center gap-3 rounded-lg border border-emerald-200 border-l-4 border-l-emerald-500 bg-emerald-50 py-2.5 pr-3 pl-4 text-sm font-medium text-emerald-800 shadow-lg animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <CheckCircle2 className="size-4 shrink-0" />
      <span>{message}</span>
      <Button variant="ghost" size="icon-xs" className="text-emerald-700" onClick={onClose}>
        <span className="sr-only">关闭</span>✕
      </Button>
    </div>
  );
}

export function IconButton({
  variant = "ghost",
  size = "icon",
  ...props
}: ComponentProps<typeof Button>) {
  return <Button variant={variant} size={size} {...props} />;
}

export function LinkButton({
  to,
  children,
  variant = "default",
  size = "default",
  className
}: {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}) {
  return (
    <Button asChild variant={variant} size={size} className={className}>
      <Link to={to}>{children}</Link>
    </Button>
  );
}

export function TextLink({
  to,
  children,
  className
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "group inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80",
        className
      )}
    >
      {children}
      <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className
}: {
  value: T;
  onChange(value: T): void;
  options: ReadonlyArray<{ value: T; label: ReactNode }>;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(next) => {
        if (next) onChange(next as T);
      }}
      aria-label={ariaLabel}
      className={cn("inline-flex gap-1 rounded-lg bg-muted p-1", className)}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          className="rounded-md px-3 text-xs data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
  className
}: {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("grid gap-1.5", className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return "-";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(new Date(value));
}

export function isOnline(expiresAt: string | null | undefined): boolean {
  return Boolean(expiresAt && Date.parse(expiresAt) > Date.now());
}
